import { nameFromCC } from 'tf-post-parser';
import {
  faHotel,
  faPlaneDeparture,
  faShield,
  faTicket,
  faSimCard,
  faBus,
  faCar,
  faShoePrints,
  faComments,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from '@mui/material';
import Link from 'next/link';
import { getUser } from 'helpers/token';
import React, { useEffect, useState } from 'react';

export default function TravelResources({
  countryCode,
  setChatOpen,
  useH2,
  showRemoveAds,
  author,
}) {
  const [user, setUser] = useState(null);

  useEffect(() => {
    setUser(getUser());
  }, []);

  const ressources = [
    {
      type: 'Flights',
      icon: faPlaneDeparture,
      text: (
        <span>
          We recommend checking{' '}
          <a
            href="https://kiwi.tp.st/B2ad4zOT"
            rel="sponsored noreferrer nofollow"
            target="_blank"
          >
            Kiwi.com
          </a>{' '}
          to find the best and cheapest flights{' '}
          {countryCode ? ` to ${nameFromCC(countryCode)}` : ''}.
        </span>
      ),
      countriesToHide: ['cu'],
    },
    {
      type: 'Accomodation',
      icon: faHotel,
      text: (
        <span>
          {countryCode
            ? `Explore the best places to stay in ${nameFromCC(countryCode)}`
            : 'Find great stays'}{' '}
          on{' '}
          <a
            href="https://www.booking.com/index.html?aid=8014892"
            rel="sponsored noreferrer nofollow"
            target="_blank"
          >
            Booking.com
          </a>
          ,{' '}
          <a
            href="https://agoda.tp.st/elW8GohJ"
            rel="sponsored noreferrer nofollow"
            target="_blank"
          >
            Agoda
          </a>{' '}
          and{' '}
          <a
            href="https://hostelworld.tp.st/ANPDOIh2"
            rel="sponsored noreferrer nofollow"
            target="_blank"
          >
            Hostelworld
          </a>
          .
        </span>
      ),
    },
    {
      type: 'Travel Insurance',
      icon: faShield,
      text: (
        <span>
          Medical emergencies abroad can be pricey, but travel health insurance
          is not. We always use{' '}
          <a
            href="https://safetywing.com/nomad-insurance/?referenceID=travelfeed&campaign=travel-resources&utm_source=travelfeed&utm_medium=Ambassador"
            rel="sponsored noreferrer nofollow"
            target="_blank"
          >
            SafetyWing
          </a>{' '}
          for affordable and reliable coverage.
        </span>
      ),
      countriesToHide: ['by', 'cu', 'ir', 'kp', 'ru', 'sy', 'ua'],
    },
    {
      type: 'Transportation',
      icon: faBus,
      text: (
        <span>
          Use{' '}
          <a
            href="https://12go.tp.st/rioEGvek"
            rel="sponsored noreferrer nofollow"
            target="_blank"
          >
            12go
          </a>{' '}
          and{' '}
          <a
            href="https://omio.tp.st/IOYADJSR"
            rel="sponsored noreferrer nofollow"
            target="_blank"
          >
            Omio
          </a>{' '}
          to find detailed bus and train schedules, making travel planning
          easier.
        </span>
      ),
    },
    {
      type: 'Car Rental',
      icon: faCar,
      text: (
        <span>
          For hassle-free car hiring,{' '}
          <a
            href="https://discovercars.tp.st/yQKbm54Z"
            rel="sponsored noreferrer nofollow"
            target="_blank"
          >
            DiscoverCars
          </a>{' '}
          is our trusted choice with a wide selection of vehicles.
        </span>
      ),
      countriesToHide: ['cu'],
    },
    {
      type: 'Internet',
      icon: faSimCard,
      text: (
        <span>
          Got an eSIM compatible phone?{' '}
          <a
            href="https://airalo.tp.st/Ir62Cvep"
            rel="sponsored noreferrer nofollow"
            target="_blank"
          >
            Airalo
          </a>{' '}
          is perfect for reliable internet access during your trip. Just install
          it before you go, and you&apos;re set!
        </span>
      ),
      countriesToHide: ['sy'],
    },
    {
      type: 'Day Trips & Tours',
      icon: faShoePrints,
      text: (
        <span>
          We recommend{' '}
          <a
            href="https://getyourguide.tp.st/1D5a7vQn"
            rel="sponsored noreferrer nofollow"
            target="_blank"
          >
            GetYourGuide
          </a>{' '}
          for a variety of well-organized and enjoyable activities.
        </span>
      ),
    },
    {
      type: 'Tickets',
      icon: faTicket,
      text: (
        <span>
          Save on entrance fees
          {countryCode ? ` in ${nameFromCC(countryCode)}` : ''} with{' '}
          <a
            href="https://klook.tp.st/ags6BzhT"
            rel="sponsored noreferrer nofollow"
            target="_blank"
          >
            Klook
          </a>{' '}
          and{' '}
          <a
            href="https://tiqets.tp.st/Lyf4ymwK"
            rel="sponsored noreferrer nofollow"
            target="_blank"
          >
            Tiquets
          </a>
          .
        </span>
      ),
    },
    {
      type: 'Travel Planner',
      icon: faComments,
      text: (
        <span>
          Need a hand planning? Our free travel planner chatbot is your personal
          guide{countryCode ? ` to ${nameFromCC(countryCode)}` : ''}.{' '}
          <a
            onClick={() => setChatOpen(true)}
            onKeyDown={() => setChatOpen(true)}
            role="button"
            tabIndex={0}
            className="tw-cursor-pointer"
          >
            Chat now
          </a>
          .
        </span>
      ),
    },
  ];

  return (
    <>
      <div className="fullwidth tw-bg-primary-50 dark:tw-bg-primary-900 tw-h-full">
        <div className="postCardContent postcontent">
          {showRemoveAds && user === author && (
            <p>
              <Link href="/dashboard/hosting">Upgrade to a paid plan</Link> to
              remove ads
            </p>
          )}
          <Typography
            color="textPrimary"
            variant="h5"
            component={useH2 ? 'h2' : 'p'}
            className="tw-font-bold tw-pt-2"
          >
            Travel Resources
            {countryCode ? ` for your trip to ${nameFromCC(countryCode)}` : ''}
          </Typography>
          <p className="tw-text-xs tw-pb-3 tw-text-gray-700 dark:tw-text-gray-100">
            Recommended by TravelFeed
          </p>
          <div className="tw-flex tw-flex-col tw-gap-6">
            {ressources.map(ressource => {
              if (
                ressource.countriesToHide &&
                ressource.countriesToHide.indexOf(countryCode) !== -1
              )
                return <></>;
              return (
                <div
                  key={ressource.type}
                  className="tw-flex tw-items-center tw-gap-4"
                >
                  <FontAwesomeIcon
                    icon={ressource.icon}
                    className="tw-h-8 tw-w-8 tw-flex-shrink-0 tw-text-primary-900 dark:tw-text-primary-100"
                  />
                  <div>
                    <p className="tw-m-0">
                      <strong className="tw-text-primary-900 dark:tw-text-primary-100">
                        {ressource.type}:
                      </strong>{' '}
                      <span>{ressource.text}</span>
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
